/**
 * Which of a venue's contacts its "last contact" date speaks for.
 *
 * A venue is worth chasing when nobody *reachable* has been written to lately, so the date is
 * taken from one tier of contacts rather than from all of them: the active ones if the venue has
 * any, else its gatekeepers, else whoever is left (expired / do-not-contact). The tier is picked
 * by which contacts exist, not by which happen to carry a date — a venue whose active contact has
 * never been written to reads "Never" instead of borrowing a gatekeeper's date.
 *
 * The backend applies the same rule in the venues list pipeline (`_last_contact_pool_expr` in
 * `venue_search_service.py`) and returns the resulting tier as `last_contact_source`, so the table
 * does not recompute it. This module is for the places holding raw contacts — the contact overlay
 * — plus the wording both surfaces use. See `docs/LAST_CONTACT.md`.
 */
import type { Communication, Contact, SlimContact, Venue } from '../types';

type ContactFlags = Contact['flags'];
type LastContactSource = NonNullable<Venue['last_contact_source']>;

/** Flags a reachable contact carries — the mirror of the backend's `_ACTIVE_FLAGS`. */
const ACTIVE_FLAGS: ContactFlags[] = ['none', 'auto_discovered'];

function isActive(contact: { flags?: ContactFlags }): boolean {
  return ACTIVE_FLAGS.includes(contact.flags ?? 'none');
}

function isGatekeeper(contact: { flags?: ContactFlags }): boolean {
  return contact.flags === 'gatekeeper';
}

/**
 * The tier of *contacts* whose communications count, and its name. An empty input yields an empty
 * pool and no source, which reads as "Never" with nothing to explain.
 */
export function lastContactPool<T extends Contact | SlimContact>(
  contacts: T[],
): { pool: T[]; source: LastContactSource | undefined } {
  const active = contacts.filter(isActive);
  if (active.length > 0) return { pool: active, source: 'active' };

  const gatekeepers = contacts.filter(isGatekeeper);
  if (gatekeepers.length > 0) return { pool: gatekeepers, source: 'gatekeeper' };

  return { pool: contacts, source: contacts.length > 0 ? 'other' : undefined };
}

/**
 * How many messages one communication holds in each direction. The browser twin of the backend's
 * `count_comm_directions` (`app/models/communication.py`) and of the aggregation expression the
 * venues list uses (`app/utils/comm_counts.py`) — change one, change the others.
 *
 * A `scheduled`, `failed` or `planned` email counts as neither: it has not gone out.
 */
function countCommDirections(comm: Communication): { sent: number; received: number } {
  switch (comm.type) {
    case 'email':
      if (comm.email?.status === 'sent') return { sent: 1, received: 0 };
      if (comm.email?.status === 'received') return { sent: 0, received: 1 };
      return { sent: 0, received: 0 };
    case 'thread': {
      const messages = comm.thread?.messages ?? [];
      return {
        sent: messages.filter(m => m.direction === 'sent').length,
        received: messages.filter(m => m.direction === 'received').length,
      };
    }
    case 'phone':
    case 'other': {
      const direction = (comm.type === 'phone' ? comm.phone : comm.other)?.direction ?? 'received';
      return direction === 'sent' ? { sent: 1, received: 0 } : { sent: 0, received: 1 };
    }
    case 'form':
      return { sent: 1, received: 0 };
    default:
      return { sent: 0, received: 0 };
  }
}

/**
 * Messages exchanged with a tier of contacts, in each direction — the `Sent` / `Received` twin of
 * the last-contact date. Pass the pool from {@link lastContactPool}, never the whole contact
 * list: the two numbers and the date answer for the same contacts, which is what stops a venue
 * reading "12 sent" beside "Never".
 */
export function countPoolDirections(pool: Contact[]): { sent: number; received: number } {
  return pool.reduce(
    (totals, contact) => {
      for (const comm of contact.communications ?? []) {
        const { sent, received } = countCommDirections(comm);
        totals.sent += sent;
        totals.received += received;
      }
      return totals;
    },
    { sent: 0, received: 0 },
  );
}

/**
 * The extra tooltip line on a date that did not come from an active contact, so a same-looking
 * date is not read as "we reached someone who can book us". Active dates need no note.
 */
export const LAST_CONTACT_SOURCE_NOTE: Record<LastContactSource, string | undefined> = {
  active: undefined,
  gatekeeper: 'via a gatekeeper contact',
  other: 'via an expired / do-not-contact contact',
};
